'use client'

/**
 * MarkdownEditor — minimal self-contained WYSIWYG markdown editor.
 *
 * Replaces `react-markdown-editor-lite` which depended on an external
 * font-awesome CDN that often failed to load, leaving the toolbar invisible.
 * This implementation uses lucide-react icons (already in the bundle) and
 * markdown-it for the preview pane.
 */

import { useCallback, useRef, useState } from 'react'
import MarkdownIt from 'markdown-it'
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Code,
  Link as LinkIcon,
  Quote,
  Eye,
  Pencil,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  height?: number
}

const mdParser = new MarkdownIt({ html: false, linkify: true, breaks: true })

type Wrap = { before: string; after?: string; placeholder?: string }
type Prefix = { prefix: string; placeholder?: string }

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Enter text...',
  height = 250,
}: MarkdownEditorProps) {
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const [tab, setTab] = useState<'edit' | 'preview'>('edit')

  const wrapSelection = useCallback(
    ({ before, after = before, placeholder: ph = '' }: Wrap) => {
      const ta = taRef.current
      if (!ta) return
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const selected = value.slice(start, end) || ph
      const next = value.slice(0, start) + before + selected + after + value.slice(end)
      onChange(next)
      requestAnimationFrame(() => {
        ta.focus()
        const caret = start + before.length + selected.length
        ta.setSelectionRange(caret, caret)
      })
    },
    [value, onChange],
  )

  const prefixLines = useCallback(
    ({ prefix, placeholder: ph = '' }: Prefix) => {
      const ta = taRef.current
      if (!ta) return
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const lineStart = value.lastIndexOf('\n', start - 1) + 1
      const slice = value.slice(lineStart, end)
      const lines = slice.length ? slice.split('\n') : [ph]
      const updated = lines.map(l => (l.startsWith(prefix) ? l : prefix + l)).join('\n')
      const next = value.slice(0, lineStart) + updated + value.slice(end)
      onChange(next)
      requestAnimationFrame(() => {
        ta.focus()
        const caret = lineStart + updated.length
        ta.setSelectionRange(caret, caret)
      })
    },
    [value, onChange],
  )

  const insertLink = useCallback(() => {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end) || 'link text'
    const url = window.prompt('URL', 'https://')
    if (!url) return
    const md = `[${selected}](${url})`
    const next = value.slice(0, start) + md + value.slice(end)
    onChange(next)
    requestAnimationFrame(() => {
      ta.focus()
      ta.setSelectionRange(start + md.length, start + md.length)
    })
  }, [value, onChange])

  const tools: Array<{
    icon: typeof Bold
    label: string
    run: () => void
  }> = [
    { icon: Bold, label: 'Bold (Ctrl+B)', run: () => wrapSelection({ before: '**', placeholder: 'bold text' }) },
    { icon: Italic, label: 'Italic (Ctrl+I)', run: () => wrapSelection({ before: '_', placeholder: 'italic text' }) },
    { icon: Heading1, label: 'Heading 1', run: () => prefixLines({ prefix: '# ', placeholder: 'Heading' }) },
    { icon: Heading2, label: 'Heading 2', run: () => prefixLines({ prefix: '## ', placeholder: 'Heading' }) },
    { icon: Heading3, label: 'Heading 3', run: () => prefixLines({ prefix: '### ', placeholder: 'Heading' }) },
    { icon: List, label: 'Bulleted list', run: () => prefixLines({ prefix: '- ', placeholder: 'list item' }) },
    { icon: ListOrdered, label: 'Numbered list', run: () => prefixLines({ prefix: '1. ', placeholder: 'list item' }) },
    { icon: Quote, label: 'Quote', run: () => prefixLines({ prefix: '> ', placeholder: 'quote' }) },
    { icon: Code, label: 'Inline code', run: () => wrapSelection({ before: '`', placeholder: 'code' }) },
    { icon: LinkIcon, label: 'Link', run: insertLink },
  ]

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return
    const key = e.key.toLowerCase()
    if (key === 'b') {
      e.preventDefault()
      wrapSelection({ before: '**', placeholder: 'bold text' })
    } else if (key === 'i') {
      e.preventDefault()
      wrapSelection({ before: '_', placeholder: 'italic text' })
    } else if (key === 'k') {
      e.preventDefault()
      insertLink()
    }
  }

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden flex flex-col">
      <div className="flex items-center justify-between border-b border-border bg-muted/40 px-2 py-1.5">
        <div className="flex items-center gap-0.5 flex-wrap">
          {tools.map(({ icon: Icon, label, run }) => (
            <button
              key={label}
              type="button"
              title={label}
              onClick={run}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setTab('edit')}
            className={cn(
              'inline-flex h-7 px-2 items-center gap-1 rounded text-xs transition-colors',
              tab === 'edit' ? 'bg-background border border-border text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
            title="Edit"
          >
            <Pencil className="h-3 w-3" /> Write
          </button>
          <button
            type="button"
            onClick={() => setTab('preview')}
            className={cn(
              'inline-flex h-7 px-2 items-center gap-1 rounded text-xs transition-colors',
              tab === 'preview' ? 'bg-background border border-border text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
            title="Preview"
          >
            <Eye className="h-3 w-3" /> Preview
          </button>
        </div>
      </div>

      {tab === 'edit' ? (
        <textarea
          ref={taRef}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          spellCheck
          style={{ height: `${height}px`, resize: 'vertical' }}
          className="w-full bg-background text-sm text-foreground placeholder:text-muted-foreground/60 px-3 py-2 font-mono leading-relaxed outline-none focus:ring-0"
        />
      ) : (
        <div
          style={{ height: `${height}px`, overflowY: 'auto' }}
          className="markdown-preview prose prose-sm dark:prose-invert max-w-none px-3 py-2 text-sm"
          dangerouslySetInnerHTML={{ __html: value ? mdParser.render(value) : '<p class="text-muted-foreground italic">Nothing to preview.</p>' }}
        />
      )}
    </div>
  )
}
